import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import ToolHelpButton from '../../../../components/ToolHelpButton';
import { useApi } from '../../../../hooks/useApi';
import { useUrlParams } from '../../../../hooks/useUrlParams';
import logger from '../../../../utils/logger';
import { fmtDateTime } from '../../../../utils/ruDates';
import {
  econBreakdown,
  econCogsList,
  econCogsSave,
  econDashboard,
  econOtherCreate,
  econOtherDelete,
  econOtherList,
  econOtherUpdate,
  econProjectBreakdown,
  econProjectDashboard,
  econSettingsGet,
  econSettingsSave,
  econSync,
  econSyncState,
} from '../../../../services/ozonUnitEconomics';
import './OzonUnitEconomics.css';
import { matchesSearch } from '../../../../utils/search';

const MSK_OFFSET_MIN = 180;

/** Сегодня по Москве: Ozon режет сутки по МСК, локальная дата браузера не подходит. */
const mskToday = () => {
  const now = new Date();
  return new Date(now.getTime() + (MSK_OFFSET_MIN + now.getTimezoneOffset()) * 60000);
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const shift = (d, n) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };
const ru = (isoStr) => {
  if (!isoStr) return '';
  const [y, m, d] = isoStr.split('-');
  return `${d}.${m}.${y}`;
};
const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];

/**
 * Пресеты заканчиваются вчерашним днём: начисления за текущие сутки Ozon ещё
 * досчитывает, и включённый «сегодня» занижает выручку и ломает сравнение
 * с прошлым периодом. Сегодняшний день доступен отдельной кнопкой.
 */
const buildPresets = () => {
  const today = mskToday();
  const yest = shift(today, -1);
  const prevMonthLast = new Date(today.getFullYear(), today.getMonth(), 0);
  const prevMonthFirst = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return [
    { key: 'today', label: 'Сегодня', from: iso(today), to: iso(today), partial: true },
    { key: 'yesterday', label: 'Вчера', from: iso(yest), to: iso(yest) },
    { key: '7d', label: '7 дней', from: iso(shift(yest, -6)), to: iso(yest) },
    { key: '14d', label: '14 дней', from: iso(shift(yest, -13)), to: iso(yest) },
    { key: '30d', label: '30 дней', from: iso(shift(yest, -29)), to: iso(yest) },
    // 1-го числа в текущем месяце ещё нет закрытых дней: период вышел бы с концом раньше начала.
    ...(today.getDate() > 1 ? [{
      key: 'month',
      label: `${MONTHS[today.getMonth()]} (с начала месяца)`,
      from: iso(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: iso(yest),
    }] : []),
    {
      key: 'prev_month',
      label: MONTHS[prevMonthFirst.getMonth()],
      from: iso(prevMonthFirst),
      to: iso(prevMonthLast),
    },
  ];
};

const money = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString('ru-RU')} ₽`);
const units = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString('ru-RU')} шт`);
const pct = (v) => (v == null ? '—' : `${v.toFixed(1).replace('.', ',')} %`);
const signed = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(0)} %`);
/** Подпись «к прошлому периоду» под плиткой; без базы сравнения — пусто. */
const vsPrev = (v, unit = '%') => (v == null
  ? null
  : `${v > 0 ? '+' : ''}${v.toFixed(1).replace('.', ',')} ${unit} к прошлому периоду`);

const TAX_MODES = [
  ['none', 'Без налога'],
  ['usn_income', 'УСН «Доходы»'],
  ['usn_income_expense', 'УСН «Доходы минус расходы»'],
  ['ausn_income', 'АУСН «Доходы»'],
  ['ausn_income_expense', 'АУСН «Доходы минус расходы»'],
  ['osno', 'ОСНО (налог на прибыль)'],
  ['patent', 'Патент'],
  ['npd', 'НПД (самозанятость)'],
];

/** Минимальный налог от доходов: платится даже при убытке. Не настройка. */
const MIN_TAX_PERCENT = { usn_income_expense: 1, ausn_income_expense: 3 };
const DEFAULT_TAX_RATE = {
  none: 0, usn_income: 6, usn_income_expense: 15,
  ausn_income: 8, ausn_income_expense: 20, osno: 20, patent: 0, npd: 0,
};

/**
 * Плитка показателя. hint — пояснение «как считается и что означает»: без него
 * цифры вроде комиссии в 50 % от выручки читаются как ошибка расчёта.
 */
/** Текст состояния ручной очереди: что сейчас происходит с загрузкой кабинета. */
function queueText(state) {
  const q = state.queue;
  if (q.state === 'waiting') {
    const min = Math.max(1, Math.ceil((q.retry_in_seconds || 0) / 60));
    return `Ozon ограничивает частоту — продолжим автоматически через ${min} мин`;
  }
  if (q.state === 'running') {
    return `Загружаю данные Ozon… ${state.loaded_days ?? 0} из ${state.expected_days ?? '?'}`;
  }
  return q.ahead > 0 ? `В очереди на загрузку, впереди: ${q.ahead}` : 'В очереди на загрузку…';
}

function Tile({ label, value, sub, tone, hint }) {
  return (
    <div className={`ue-tile${tone ? ` ue-tile--${tone}` : ''}`} title={hint || undefined}>
      <div className="ue-tile__label">
        {label}
        {hint ? <span className="ue-tile__hint">?</span> : null}
      </div>
      <div className="ue-tile__value">{value}</div>
      {sub ? <div className="ue-tile__sub">{sub}</div> : null}
    </div>
  );
}

const EMPTY_EXPENSE = {
  title: '', amount: '', kind: 'monthly',
  spend_date: null, active_from: null, active_to: null, is_counted: true, sort_order: 0,
};

function OzonUnitEconomics({ ozonAccountId, projectCabinets = null }) {
  const { request, showStatus } = useApi();
  // Уровень проекта: только просмотр сводки по кабинетам; синхронизация, налоги и себестоимость правятся в кабинете.
  const projectIds = useMemo(() => (projectCabinets || []).map((c) => c.id), [projectCabinets]);
  const projectMode = !ozonAccountId && projectIds.length > 0;
  const scopeKey = ozonAccountId || (projectMode ? `project:${projectIds.join(',')}` : null);

  const presets = useMemo(buildPresets, []);
  // Ссылка из уведомления («прибыль упала») открывает ровно тот период, о котором предупредили: ?from=&to=.
  const { get: getUrlParam } = useUrlParams();
  const urlFrom = getUrlParam('from');
  const urlTo = getUrlParam('to');
  const urlPeriodValid = /^\d{4}-\d{2}-\d{2}$/.test(urlFrom) && /^\d{4}-\d{2}-\d{2}$/.test(urlTo) && urlFrom <= urlTo;
  const [presetKey, setPresetKey] = useState(urlPeriodValid ? 'custom' : '7d');
  const [range, setRange] = useState(() => {
    if (urlPeriodValid) return { from: urlFrom, to: urlTo };
    const p = buildPresets().find((x) => x.key === '7d');
    return { from: p.from, to: p.to };
  });
  useEffect(() => {
    if (urlPeriodValid) {
      setPresetKey('custom');
      setRange({ from: urlFrom, to: urlTo });
    }
  }, [urlPeriodValid, urlFrom, urlTo]);
  const { from: dateFrom, to: dateTo } = range;

  const [sheet, setSheet] = useState('dashboard');
  const [groupBy, setGroupBy] = useState('sku');

  const [dashboard, setDashboard] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [syncState, setSyncState] = useState(null);
  const [settings, setSettings] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [draft, setDraft] = useState(EMPTY_EXPENSE);
  const [cogs, setCogs] = useState(null);
  const [cogsEdits, setCogsEdits] = useState({});
  const [cogsSearch, setCogsSearch] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const cogsFileRef = useRef(null);
  const pollRef = useRef(null);
  useEffect(() => () => clearTimeout(pollRef.current), []);

  /**
   * Ключ текущего периода. Загрузка и опрос синхронизации асинхронные, и без
   * этой проверки ответ по прежнему периоду приходит уже после смены дат и
   * затирает новые цифры: на экране оказывается август в заголовке и неделя
   * в данных.
   */
  const periodKey = `${scopeKey}|${dateFrom}|${dateTo}`;
  const periodRef = useRef(periodKey);

  const dayCount = useMemo(() => {
    if (!dateFrom || !dateTo) return 0;
    return Math.round((new Date(dateTo) - new Date(dateFrom)) / 86400000) + 1;
  }, [dateFrom, dateTo]);

  const includesToday = dateTo === iso(mskToday());

  const applyPreset = (p) => {
    setPresetKey(p.key);
    setRange({ from: p.from, to: p.to });
  };

  const loadDashboard = useCallback(async () => {
    if (!scopeKey) return;
    const key = `${scopeKey}|${dateFrom}|${dateTo}`;
    setBusy(true);
    try {
      const data = projectMode
        ? await econProjectDashboard({ request, accountIds: projectIds, dateFrom, dateTo })
        : await econDashboard({ request, accountId: ozonAccountId, dateFrom, dateTo });
      if (periodRef.current !== key) return;
      setDashboard(data);
    } catch (e) {
      logger.error('[unit-economics] dashboard failed', e);
      showStatus('error', 'Не удалось загрузить дашборд');
    } finally {
      setBusy(false);
    }
  }, [scopeKey, dateFrom, dateTo, request, showStatus]);

  const loadBreakdown = useCallback(async () => {
    if (!scopeKey) return;
    const key = `${scopeKey}|${dateFrom}|${dateTo}`;
    setBusy(true);
    try {
      const data = projectMode
        ? await econProjectBreakdown({ request, accountIds: projectIds, dateFrom, dateTo, groupBy })
        : await econBreakdown({ request, accountId: ozonAccountId, dateFrom, dateTo, groupBy });
      if (periodRef.current !== key) return;
      setBreakdown(data);
    } catch (e) {
      logger.error('[unit-economics] breakdown failed', e);
      showStatus('error', 'Не удалось загрузить детализацию');
    } finally {
      setBusy(false);
    }
  }, [scopeKey, dateFrom, dateTo, groupBy, request, showStatus]);

  const loadFinanceSetup = useCallback(async () => {
    if (!ozonAccountId) return;
    try {
      const [s, e] = await Promise.all([
        econSettingsGet({ request, accountId: ozonAccountId }),
        econOtherList({ request, accountId: ozonAccountId, dateFrom, dateTo }),
      ]);
      setSettings(s);
      setExpenses(e);
    } catch (err) {
      logger.error('[unit-economics] settings failed', err);
      showStatus('error', 'Не удалось загрузить настройки налогов');
    }
  }, [ozonAccountId, dateFrom, dateTo, request, showStatus]);

  const loadCogs = useCallback(async () => {
    if (!ozonAccountId) return;
    setBusy(true);
    try {
      const key = `${ozonAccountId}|${dateFrom}|${dateTo}`;
      const data = await econCogsList({ request, accountId: ozonAccountId, dateFrom, dateTo });
      if (periodRef.current !== key) return;
      setCogs(data);
      // Себестоимость, заполненная только под прежним артикулом, сразу
      // предлагается к сохранению под текущим — чтобы её видел и Репрайсер.
      const carried = {};
      (data?.rows || []).forEach((r) => {
        if (r.cogs_from_old_offer && r.cogs != null) carried[r.offer_id] = String(r.cogs);
      });
      setCogsEdits(carried);
    } catch (e) {
      logger.error('[unit-economics] cogs failed', e);
      showStatus('error', 'Не удалось загрузить себестоимость');
    } finally {
      setBusy(false);
    }
  }, [ozonAccountId, dateFrom, dateTo, request, showStatus]);

  const COGS_SHEET = 'Себестоимость';
  const COGS_COLS = { sku: 'SKU', offer: 'Артикул', name: 'Товар', cogs: 'Себестоимость, ₽' };

  /**
   * Шаблон выгружается со всеми товарами и уже заполненными значениями, чтобы
   * правку можно было делать поверх текущих данных, а не набивать заново.
   */
  const downloadCogsTemplate = () => {
    const rows = (cogs?.rows || []).map((r) => ({
      [COGS_COLS.sku]: r.sku || '',
      [COGS_COLS.offer]: r.offer_id,
      [COGS_COLS.name]: r.name,
      [COGS_COLS.cogs]: r.cogs ?? '',
    }));
    if (!rows.length) {
      showStatus('error', 'Нет товаров для выгрузки');
      return;
    }
    const sheet = XLSX.utils.json_to_sheet(rows, {
      header: [COGS_COLS.sku, COGS_COLS.offer, COGS_COLS.name, COGS_COLS.cogs],
    });
    sheet['!cols'] = [{ wch: 14 }, { wch: 28 }, { wch: 60 }, { wch: 18 }];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, COGS_SHEET);
    XLSX.writeFile(book, `sebestoimost-${dateFrom}_${dateTo}.xlsx`, { bookType: 'xlsx' });
  };

  const importCogsXlsx = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';  // иначе повторный выбор того же файла не сработает
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const book = XLSX.read(ev.target.result, { type: 'array' });
        const sheet = book.Sheets[COGS_SHEET] || book.Sheets[book.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        // Товар узнаём по SKU: он не меняется, когда продавец переименовывает
        // артикул. Без SKU в файле — по артикулу, текущему или прежнему.
        const bySku = new Map();
        const byOffer = new Map();
        (cogs?.rows || []).forEach((r) => {
          if (r.sku) bySku.set(String(r.sku), r.offer_id);
          byOffer.set(r.offer_id, r.offer_id);
          (r.old_offer_ids || []).forEach((old) => {
            if (!byOffer.has(old)) byOffer.set(old, r.offer_id);
          });
        });
        const next = {};
        let skipped = 0;
        rows.forEach((row) => {
          const sku = String(row[COGS_COLS.sku] ?? '').trim();
          const article = String(row[COGS_COLS.offer] ?? '').trim();
          if (!sku && !article) return;
          const offer = bySku.get(sku) || byOffer.get(article);
          if (!offer) { skipped += 1; return; }
          const raw = String(row[COGS_COLS.cogs] ?? '').replace(/\s/g, '').replace(',', '.').trim();
          if (raw === '') return;
          const value = Number(raw);
          if (!Number.isFinite(value) || value < 0) { skipped += 1; return; }
          next[offer] = String(value);
        });
        if (!Object.keys(next).length) {
          showStatus('error', 'В файле не нашлось строк с себестоимостью');
          return;
        }
        setCogsEdits((prev) => ({ ...prev, ...next }));
        showStatus(
          'success',
          `Подставлено ${Object.keys(next).length} позиций${skipped ? `, пропущено ${skipped}` : ''}. `
          + 'Проверьте и нажмите «Сохранить».',
        );
      } catch (err) {
        logger.error('[unit-economics] cogs import failed', err);
        showStatus('error', 'Не удалось прочитать файл');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const saveCogs = async () => {
    const items = Object.entries(cogsEdits).map(([offer_id, value]) => ({
      offer_id,
      cogs: value === '' ? null : Number(value),
    }));
    if (!items.length) return;
    try {
      await econCogsSave({ request, accountId: ozonAccountId, items });
      showStatus('success', `Сохранено позиций: ${items.length}`);
      await loadCogs();
      await loadDashboard();
    } catch (e) {
      logger.error('[unit-economics] cogs save failed', e);
      showStatus('error', 'Не удалось сохранить себестоимость');
    }
  };

  const refreshSyncState = useCallback(async () => {
    if (!ozonAccountId) return null;
    const key = `${ozonAccountId}|${dateFrom}|${dateTo}`;
    try {
      const state = await econSyncState({ request, accountId: ozonAccountId, dateFrom, dateTo });
      if (periodRef.current !== key) return null;
      setSyncState(state);
      return state;
    } catch (e) {
      logger.error('[unit-economics] sync-state failed', e);
      return null;
    }
  }, [ozonAccountId, dateFrom, dateTo, request]);

  /** Догрузка идёт фоновой задачей, поэтому опрашиваем состояние до закрытия всех дней. */
  const runSync = useCallback(async (force = false, resume = false) => {
    if (!ozonAccountId || syncing) return;
    const key = `${ozonAccountId}|${dateFrom}|${dateTo}`;
    setSyncing(true);
    // resume — загрузка уже стоит в очереди на сервере, только следим за ней
    if (!resume) {
      try {
        await econSync({ request, accountId: ozonAccountId, dateFrom, dateTo, force });
      } catch (e) {
        logger.error('[unit-economics] sync failed', e);
        showStatus('error', 'Не удалось поставить загрузку в очередь');
        setSyncing(false);
        return;
      }
    }
    let attempts = 0;
    let stalled = 0;
    let prev = '';
    const poll = async () => {
      if (periodRef.current !== key) return;  // период сменили — опрос неактуален
      attempts += 1;
      const state = await refreshSyncState();
      if (periodRef.current !== key) return;
      // Задача в фоне ничего не сообщает о своём конце, поэтому конец ловим по
      // остановке прогресса: иначе при ошибках спиннер крутится шесть минут.
      const sig = state
        ? `${state.loaded_days}|${state.error_days}|${state.missing_days}|${state.last_synced_at || ''}`
        : '';
      stalled = sig && sig === prev ? stalled + 1 : 0;
      prev = sig;
      // Пока кабинет стоит в очереди на сервере, сервер сам дожимает остаток при
      // лимитах Ozon: ждём его, а не считаем остановку прогресса сбоем.
      const queued = Boolean(state && state.queue);
      const done = state && !queued && state.missing_days === 0 && state.error_days === 0;
      if (queued) stalled = 0;
      if (done || (!queued && (stalled >= 20 || attempts > 120)) || attempts > 1200) {
        setSyncing(false);
        await loadDashboard();
        if (sheet === 'breakdown') await loadBreakdown();
        return;
      }
      pollRef.current = setTimeout(poll, queued ? 5000 : 3000);
    };
    pollRef.current = setTimeout(poll, 2000);
  }, [ozonAccountId, dateFrom, dateTo, syncing, request, showStatus,
      refreshSyncState, loadDashboard, loadBreakdown, sheet]);

  useEffect(() => {
    // Период сменился: гасим прошлый опрос и убираем чужие цифры с экрана,
    // иначе новые даты какое-то время стоят над старыми данными.
    periodRef.current = periodKey;
    clearTimeout(pollRef.current);
    setSyncing(false);
    setDashboard(null);
    setBreakdown(null);
    setCogs(null);

    let cancelled = false;
    if (projectMode) {
      loadDashboard();
      return undefined;
    }
    (async () => {
      const state = await refreshSyncState();
      if (cancelled || !state) return;
      // Дни с ошибкой догружаем один раз при открытии периода: сами по себе
      // они не перезапрашиваются, и экран оставался с неполными цифрами.
      if (state.queue) { await loadDashboard(); await runSync(false, true); }
      else if (state.missing_days > 0 || state.error_days > 0) await runSync(false);
      else await loadDashboard();
    })();
    return () => { cancelled = true; };
    // Зависим только от кабинета и периода: коллбэки пересоздаются на каждый
    // рендер, и добавление их в список запустило бы догрузку по кругу.
  }, [scopeKey, dateFrom, dateTo]);

  useEffect(() => {
    if (sheet === 'breakdown') loadBreakdown();
    if (sheet === 'setup' && !projectMode) loadFinanceSetup();
    if (sheet === 'cogs' && !projectMode) loadCogs();
    // Коллбэки намеренно вне зависимостей — см. комментарий выше.
  }, [sheet, groupBy, dateFrom, dateTo]);

  // ── иные расходы ──────────────────────────────────────────────────────────
  const saveExpense = async (row, patch) => {
    const payload = {
      title: row.title, amount: row.amount, kind: row.kind,
      spend_date: row.spend_date, active_from: row.active_from, active_to: row.active_to,
      is_counted: row.is_counted, sort_order: row.sort_order, ...patch,
    };
    try {
      await econOtherUpdate({ request, accountId: ozonAccountId, id: row.id, payload });
      await loadFinanceSetup();
      await loadDashboard();
    } catch (e) {
      logger.error('[unit-economics] expense save failed', e);
      showStatus('error', 'Не удалось сохранить расход');
    }
  };

  const addExpense = async () => {
    if (!draft.title.trim() || !Number(draft.amount)) {
      showStatus('error', 'Заполните название и сумму');
      return;
    }
    try {
      await econOtherCreate({
        request,
        accountId: ozonAccountId,
        payload: { ...draft, amount: Number(draft.amount) },
      });
      setDraft(EMPTY_EXPENSE);
      await loadFinanceSetup();
      await loadDashboard();
    } catch (e) {
      logger.error('[unit-economics] expense create failed', e);
      showStatus('error', 'Не удалось добавить расход');
    }
  };

  const removeExpense = async (row) => {
    try {
      await econOtherDelete({ request, accountId: ozonAccountId, id: row.id });
      await loadFinanceSetup();
      await loadDashboard();
    } catch (e) {
      logger.error('[unit-economics] expense delete failed', e);
      showStatus('error', 'Не удалось удалить расход');
    }
  };

  const saveSettings = async (patch) => {
    const payload = { ...settings, ...patch };
    delete payload.tax_modes;
    delete payload.tax_mode_label;
    const previous = settings;
    setSettings((prev) => ({ ...prev, ...patch }));
    try {
      await econSettingsSave({ request, accountId: ozonAccountId, payload });
      await loadDashboard();
    } catch (e) {
      logger.error('[unit-economics] settings save failed', e);
      setSettings(previous);  // на экране — то, что реально сохранено
      showStatus('error', 'Не удалось сохранить настройки');
    }
  };

  // ── колонки детализации ───────────────────────────────────────────────────
  const columns = useMemo(() => {
    const rub = (key, header, hint, width = 130) => ({
      key, header, hint, width, type: 'number', align: 'right', summary: 'sum',
      format: (v) => (v == null ? '—' : Math.round(v).toLocaleString('ru-RU')),
    });
    const perc = (key, header, hint, width = 160) => ({
      key, header, hint, width, type: 'number', align: 'right',
      format: (v) => (v == null ? '—' : v.toFixed(1).replace('.', ',')),
    });
    const head = groupBy === 'model'
      ? [{ key: 'name', header: 'Модель', width: 280, type: 'text', sticky: true,
           hint: 'Значение атрибута «Название модели» из карточки. Строки всех артикулов модели сложены.' }]
      : [
          { key: 'image', header: 'Фото', width: 64, type: 'image', sortable: false, sticky: true },
          { key: 'offer_id', header: 'Артикул', width: 160, type: 'text', sticky: true,
            hint: 'Текущий артикул (offer_id) из каталога. Товар определяется по SKU, '
              + 'поэтому переименованный артикул не раздваивает строку и себестоимость.' },
          { key: 'name', header: 'Товар', width: 300, type: 'text' },
          { key: 'model_name', header: 'Модель', width: 180, type: 'text' },
        ];
    return [
      ...head,
      { key: 'units', header: 'Продано, шт', width: 110, type: 'number', align: 'right',
        summary: 'sum',
        hint: 'Единиц, по которым Ozon начислил комиссию за продажу, то есть фактически выкупленных.' },
      { key: 'returns_units', header: 'Возвраты, шт', width: 115, type: 'number',
        align: 'right', summary: 'sum',
        hint: 'Единиц, вернувшихся после выкупа. В «Продано» они не входят, а выручка '
            + 'и себестоимость уже уменьшены на них.' },
      rub('returns_amount', 'Возвраты, ₽',
          'Сумма сторнированных продаж. Выручка показана уже за вычетом этой суммы.', 130),
      rub('revenue', 'Выручка до соинвеста, ₽',
          'Цена продавца × количество по выкупам, минус возвраты. Соинвестиция Ozon входит: '
          + 'маркетплейс её доплачивает.', 175),
      rub('revenue_sale', 'Выручка по цене реализации, ₽',
          'Сколько фактически заплатили покупатели: цена продавца минус соинвест Ozon. '
          + 'От этой суммы считаются налог и НДС.', 205),
      rub('discount', 'Соинвест Ozon, ₽',
          'Скидка за счёт Ozon. Разница между двумя выручками, ваших денег не касается.', 150),
      rub('commission', 'Комиссия, ₽',
          'Вознаграждение Ozon за продажу. Ставка уже нагружена соинвестицией, поэтому доходит '
          + 'до 50 % от выручки продавца — это цифра из отчёта, а не ошибка расчёта.'),
      rub('logistics', 'Логистика и сервис, ₽',
          'Доставка, обработка, упаковка, обратная логистика, эквайринг и рассрочка.', 165),
      rub('storage', 'Хранение, ₽',
          'Размещение товара на складах Ozon и хранение возвратов.', 120),
      rub('ads', 'Реклама, ₽',
          'Продвижение бренда и трафареты Ozon привязывает к товару. «Оплата за клик» приходит '
          + 'одной суммой без SKU и разносится по фактическим тратам из модуля Рекламы.', 120),
      rub('other', 'Прочие Ozon, ₽',
          'Штрафы, страхование остатков, утилизация, подписки и всё, что не попало в другие группы.', 140),
      rub('ozon_expenses_total', 'Расходы Ozon, ₽',
          'Комиссия + логистика + хранение + реклама + прочие. Без себестоимости и налогов.', 150),
      rub('payout', 'К перечислению, ₽',
          'Выручка минус расходы Ozon — сколько маркетплейс переведёт по этому товару.', 150),
      rub('cogs', 'Себестоимость, ₽',
          'Закупочная цена × продано. Заполняется на листе «Себестоимость», таблица общая '
          + 'с Репрайсером. Прочерк — себестоимость не заполнена.', 150),
      rub('tax', 'Налоги, ₽',
          'Доля налога и НДС кабинета, разнесённая пропорционально выручке: к товару Ozon '
          + 'их не привязывает.', 120),
      rub('other_expenses', 'Иные расходы, ₽',
          'Ваши расходы вне Ozon (офис, ФОТ), разнесённые пропорционально выручке. '
          + 'Настраиваются на листе «Налоги и расходы».', 140),
      rub('direct_expenses', 'Прямые расходы, ₽',
          'Всё, что относится именно к этому товару: расходы Ozon по его отправлениям, '
          + 'реклама по фактическим тратам и себестоимость. Исчезнет, если товар вывести.', 160),
      rub('allocated_expenses', 'Общие расходы, ₽',
          'Разнесённое пропорционально выручке: начисления Ozon без SKU, налоги, иные расходы. '
          + 'Уберёте товар — они переедут на остальные, а не исчезнут.', 160),
      rub('expenses_total', 'Расходы всего, ₽',
          'Прямые + общие. В сумме по всем строкам сходится с расходами дашборда.', 150),
      rub('profit_direct', 'Прибыль прямая, ₽',
          'Выручка минус прямые расходы. Отвечает на вопрос «держать товар или выводить»: '
          + 'если она в минусе, товар убыточен сам по себе.', 150),
      rub('profit', 'Прибыль полная, ₽',
          'Выручка минус все расходы, включая долю общих. Отвечает на вопрос «сколько заработали»; '
          + 'сумма по всем строкам сходится с дашбордом.', 150),
      perc('margin_direct', 'Маржа прямая, %',
           'Прямая прибыль ÷ выручка до соинвеста × 100.', 150),
      perc('margin_seller', 'Маржа от цены до соинвеста, %',
           'Полная прибыль ÷ выручка до соинвеста × 100.', 215),
      perc('margin_sale', 'Маржа от цены реализации, %',
           'Полная прибыль ÷ выручка по цене реализации × 100. Отдача от денег, которые '
           + 'реально заплатил покупатель.', 200),
      perc('profitability', 'Рентабельность, %',
           'Полная прибыль ÷ себестоимость × 100. Сколько заработали на каждый вложенный '
           + 'в товар рубль.', 155),
    ];
  }, [groupBy]);

  if (!scopeKey) return <div className="ue-empty">Выберите кабинет OZON.</div>;

  const s = dashboard?.sales;
  const f = dashboard?.finance;
  const e = dashboard?.expenses;
  const t = dashboard?.taxes;

  return (
    <div className="ue">
      {/* ── период ─────────────────────────────────────────────────────── */}
      <div className="ue-period">
        <div className="ue-period__presets">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`ue-period__preset${presetKey === p.key ? ' ue-period__preset--on' : ''}`}
              onClick={() => applyPreset(p)}
              title={`${ru(p.from)} — ${ru(p.to)}`}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className={`ue-period__preset${presetKey === 'custom' ? ' ue-period__preset--on' : ''}`}
            onClick={() => setPresetKey('custom')}
          >
            Свой период
          </button>
        </div>
      </div>

      <div className="ue-rangebar">
        <div className="ue-rangebar__main">
          {presetKey === 'custom' ? (
            <div className="ue-period__dates">
              <input
                type="date" value={dateFrom} max={dateTo}
                onChange={(ev) => setRange({ from: ev.target.value, to: dateTo })}
              />
              <span>—</span>
              <input
                type="date" value={dateTo} min={dateFrom} max={iso(mskToday())}
                onChange={(ev) => setRange({ from: dateFrom, to: ev.target.value })}
              />
            </div>
          ) : (
            <strong className="ue-rangebar__dates">{ru(dateFrom)} — {ru(dateTo)}</strong>
          )}
          <span className="ue-rangebar__days">{dayCount} дн.</span>
          {includesToday && (
            <span className="ue-rangebar__warn">
              сегодняшний день ещё не закрыт — Ozon досчитывает начисления
            </span>
          )}
        </div>
        <div className="ue-rangebar__side">
          {projectMode && (
            <span className="ue-period__state">Сводка по кабинетам проекта: {projectIds.length}</span>
          )}
          {!projectMode && <span className={`ue-period__state${syncState?.error_days ? ' ue-period__state--warn' : ''}`}>
            {syncState?.queue
              ? queueText(syncState)
              : syncing
              ? `Загружаю начисления… ${syncState?.loaded_days ?? 0} из ${syncState?.expected_days ?? '?'}`
              : syncState
                ? `Загружено ${syncState.loaded_days} из ${syncState.expected_days} дн.${syncState.error_days ? `, ошибок: ${syncState.error_days}` : ''}`
                : ''}
          </span>}
          {!projectMode && <button
            type="button"
            className="primary"
            // При ошибках догружаем только незакрытые дни: полная перезагрузка
            // периода снова упёрлась бы в лимиты Ozon.
            onClick={() => runSync(!(syncState?.error_days || syncState?.missing_days))}
            disabled={syncing || busy || Boolean(syncState?.queue)}
          >
            {syncState?.queue ? 'В очереди…' : syncState?.error_days ? 'Догрузить недостающие дни' : 'Обновить данные'}
          </button>}
          <ToolHelpButton toolKey="ozon_unit_economics" />
        </div>
      </div>

      {syncState?.queue && (
        <div className="ue-syncerror ue-syncerror--soft">
          Загрузка идёт в очереди на сервере: Ozon ограничивает частоту запросов, остаток
          подтянется сам. Страницу можно закрыть — ничего нажимать не нужно.
        </div>
      )}
      {!syncing && !syncState?.queue && syncState?.error_days > 0 && (
        <div className="ue-syncerror">
          <strong>Не загрузились начисления за {syncState.error_days} дн.</strong>
          {syncState.error_message && <span> {syncState.error_message}</span>}
          {syncState.error_dates?.length > 0 && (
            <span className="ue-syncerror__days">
              Дни: {syncState.error_dates.map(ru).join(', ')}
            </span>
          )}
          <span className="ue-syncerror__hint">
            Цифры ниже посчитаны по загруженным дням и будут занижены. Нажмите
            «Догрузить недостающие дни» — Ozon ограничивает частоту обращений к
            финансовым отчётам, повтор через пару минут обычно проходит.
          </span>
        </div>
      )}
      {!syncing && !syncState?.queue && !syncState?.error_days && syncState?.warning_message && (
        <div className="ue-syncerror ue-syncerror--soft">{syncState.warning_message}</div>
      )}

      {/* ── листы ──────────────────────────────────────────────────────── */}
      <div className="ue-sheets">
        {[
          ['dashboard', 'Дашборд'],
          ['breakdown', 'Экономика по товарам'],
          ['cogs', 'Себестоимость'],
          ['setup', 'Налоги и расходы'],
        ].filter(([key]) => !projectMode || key === 'dashboard' || key === 'breakdown').map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`ue-sheets__tab${sheet === key ? ' ue-sheets__tab--on' : ''}`}
            onClick={() => setSheet(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── дашборд ────────────────────────────────────────────────────── */}
      {sheet === 'dashboard' && (
        !dashboard ? (
          <div className="ue-empty">{busy || syncing ? 'Считаю…' : 'Нет данных за период.'}</div>
        ) : (
          <>
            <div className="ue-block">
              <div className="ue-block__title">Продажи</div>
              <div className="ue-tiles">
                {(() => {
                  // Заказы собираются заранее, а не при открытии: пока не все дни периода
                  // загружены, показываем прочерк, а не заниженную сумму.
                  const ready = s.orders_ready !== false;
                  const wait = `загружается: ${s.orders_loaded_days || 0} из ${s.orders_expected_days} дн.`;
                  const upd = s.orders_synced_at ? `обновлено ${fmtDateTime(s.orders_synced_at)}` : null;
                  return (
                    <>
                      <Tile label="Заказы" hint="Отправления, созданные в периоде по московским суткам. Сумма — по цене продавца. Собираются заранее, ночью и по кнопке «Синхронизировать»." value={ready ? units(s.orders_count) : '—'} sub={ready ? money(s.orders_amount) : wait} />
                      <Tile label="Товаров в заказах" hint="Единиц товара во всех заказах периода. Больше числа заказов, если в заказе несколько позиций." value={ready ? units(s.items_count) : '—'} />
                      <Tile label="Средний чек" hint="Сумма заказов ÷ количество заказов." value={ready ? money(s.avg_check) : '—'} />
                      <Tile label="Доставляются" hint="Заказы периода, которые были в пути или ждали отгрузки на момент последней синхронизации. Статусы меняются, значение обновляется при каждой синхронизации." value={ready ? units(s.delivering_count) : '—'} sub={ready ? [money(s.delivering_amount), upd].filter(Boolean).join(' · ') : wait} />
                    </>
                  );
                })()}
                <Tile
                  label="Выкупы" hint="Отправления, по которым Ozon начислил комиссию за продажу, — покупатель забрал товар. Считаются по дате начисления, поэтому выкупы периода могут относиться к более ранним заказам."
                  value={units(s.bought_count)}
                  sub={[`${money(s.bought_amount)} · ${s.bought_units} ед.`, vsPrev(s.bought_trend_pct)].filter(Boolean).join(' · ')}
                  tone="income"
                />
                <Tile label="Невыкупы" hint="Отправления с обратной логистикой, по которым продажи не было: покупатель не забрал товар." value={units(s.not_bought_count)} sub={vsPrev(s.not_bought_trend_pct)} tone="expense" />
                <Tile
                  label="Возвраты" hint="Сторнирующие строки с отрицательной суммой: товар выкупили, потом вернули. Выручка уже уменьшена на эту сумму."
                  value={units(s.returns_count)}
                  sub={[s.returns_amount ? `−${money(s.returns_amount)}` : null, vsPrev(s.returns_trend_pct)].filter(Boolean).join(' · ') || null}
                  tone="expense"
                />
                <Tile label="Процент выкупа" hint="Выкупы ÷ (выкупы + невыкупы) × 100." value={pct(s.buyout_rate)} sub={vsPrev(s.buyout_rate_delta, 'п.п.')} />
                {s.buyout_postings > 0 && (
                  <Tile
                    label="Выкуплено Ozon" hint="Трансграничные сделки: маркетплейс выкупает товар у вас, агентской продажи нет. Приходят отдельным отчётом (УПД по выкупленным товарам), в обычных начислениях их не видно."
                    value={units(s.buyout_postings)}
                    sub={`${money(s.buyout_gross)} по цене продавца · к начислению ${money(s.buyout_amount)} · скидка по категории ${money(s.buyout_discount)}`}
                    tone="income"
                  />
                )}
                <Tile
                  label="Динамика соинвеста" hint="Сравнение соинвеста Ozon с предыдущим периодом такой же длины. Считается только для окон до 31 дня."
                  value={signed(s.discount_trend_pct)}
                  sub={s.trend_available
                    ? `база ${ru(s.prev_period?.from)} — ${ru(s.prev_period?.to)}`
                    : s.trend_reason === 'prev_not_loaded'
                      ? `нет сравнения: период ${ru(s.prev_period?.from)} — ${ru(s.prev_period?.to)} ещё не загружен`
                      : 'сравнение считается для периодов до 31 дня'}
                />
                <Tile
                  label="Динамика заказов" hint="Сравнение количества заказов с предыдущим периодом такой же длины. Считается только для окон до 31 дня."
                  value={signed(s.orders_trend_pct)}
                  sub={s.trend_available
                    ? `выручка ${signed(s.revenue_trend_pct)} · база ${ru(s.prev_period?.from)} — ${ru(s.prev_period?.to)}`
                    : s.trend_reason === 'prev_not_loaded'
                      ? `нет сравнения: период ${ru(s.prev_period?.from)} — ${ru(s.prev_period?.to)} ещё не загружен`
                      : 'сравнение считается для периодов до 31 дня'}
                />
              </div>
            </div>

            <div className="ue-block">
              <div className="ue-block__title">Экономика</div>
              <div className="ue-tiles">
                <Tile
                  label="Выручка до соинвеста" hint="Цена продавца × количество по выкупам, минус возвраты. Соинвестиция Ozon входит: маркетплейс её доплачивает."
                  value={money(f.revenue)}
                  sub="по цене продавца"
                  tone="income"
                />
                <Tile
                  label="Выручка по цене реализации" hint="Сколько фактически заплатили покупатели: цена продавца минус баллы Ozon. От этой суммы считаются налог и НДС."
                  value={money(f.revenue_sale)}
                  sub="сколько заплатили покупатели"
                  tone="income"
                />
                <Tile label="Соинвест Ozon" hint="Скидка за счёт Ozon. Разница между двумя выручками, ваших денег не касается." value={money(f.discount)} sub={f.revenue ? `${pct(f.discount / f.revenue * 100)} от выручки до соинвеста` : null} tone="muted" />
                <Tile label="Расходы Ozon" hint="Комиссия, логистика, хранение, реклама и прочие начисления маркетплейса. Без себестоимости, налогов и иных расходов." value={money(f.ozon_expenses_total)} tone="expense" />
                <Tile label="К перечислению" hint="Выручка минус расходы Ozon. Контрольная сумма: совпадает с суммой всех начислений за период." value={money(f.payout)} sub="выручка минус расходы Ozon" tone="income" />
                {f.has_cogs ? (
                  <>
                    <Tile label="Себестоимость" hint="Закупочная цена × продано. Заполняется на листе «Себестоимость», таблица общая с Репрайсером." value={money(f.cogs)} tone="expense" />
                    <Tile label="Налоги" hint="НДС и налог с дохода. База — выручка по цене реализации, то есть сколько заплатил покупатель. Соинвестиция Ozon в базу не входит. НДС «в том числе»: с 1 000 ₽ при 5 % это 47,62 ₽." value={money(t?.total)} sub={t?.tax_mode_label} tone="expense" />
                    <Tile label="Иные расходы" hint="Ваши расходы вне Ozon с галочкой учёта: офис, ФОТ и прочее. Ежемесячные попадают в период пропорционально дням." value={money(dashboard.other_expenses?.counted)} tone="expense" />
                    <Tile label="Расходы всего" hint="Расходы Ozon + себестоимость + налоги + иные расходы." value={money(f.expenses_total)} tone="expense" />
                    <Tile label="Прибыль" hint="Выручка до соинвеста минус все расходы." value={money(f.profit)} tone={f.profit >= 0 ? 'income' : 'expense'} />
                    <Tile label="Маржинальность от цены до соинвеста" hint="Прибыль ÷ выручка до соинвеста × 100." value={pct(f.margin_seller)} />
                    <Tile label="Маржинальность от цены реализации" hint="Прибыль ÷ выручка по цене реализации × 100. Отдача от денег, которые реально заплатил покупатель." value={pct(f.margin_sale)} />
                    <Tile label="Рентабельность" hint="Прибыль ÷ себестоимость × 100. Сколько заработали на каждый вложенный в товар рубль." value={pct(f.profitability)} sub="прибыль к себестоимости" />
                  </>
                ) : (
                  <Tile
                    label="Прибыль и маржинальность" hint="Не считаются, пока не заполнена себестоимость: без неё прибыль была бы завышена на всю закупку."
                    value="—"
                    sub="нужна себестоимость: Репрайсер → «Цена под рентабельность»"
                    tone="muted"
                  />
                )}
              </div>
              {f.has_cogs && f.cogs_coverage < 100 && (
                <div className="ue-note">
                  Себестоимость известна для {f.cogs_coverage} % проданных единиц — на остальных
                  товарах прибыль завышена. Дозаполнить: Репрайсер → «Цена под рентабельность».
                </div>
              )}
            </div>

            <div className="ue-block">
              <div className="ue-block__title">Расходы</div>
              <div className="ue-tiles">
                <Tile label="Комиссия Ozon" hint="Вознаграждение за продажу. Ставка уже нагружена соинвестицией, поэтому доходит до 50 % от выручки продавца — это цифра из отчёта, а не ошибка расчёта." value={money(e.commission)} tone="expense" />
                <Tile label="Логистика и сервис" hint="Доставка, обработка, упаковка, обратная логистика, эквайринг и рассрочка." value={money(e.logistics)} tone="expense" />
                <Tile label="Платное хранение" hint="Размещение товара на складах Ozon и хранение возвратов." value={money(e.storage)} tone="expense" />
                <Tile label="Реклама и отзывы" hint="Оплата за клик, трафареты, продвижение бренда, работа с отзывами." value={money(e.ads)} tone="expense" />
                <Tile label="Прочие расходы Ozon" hint="Штрафы, страхование остатков, утилизация, подписки и всё, что не попало в другие группы." value={money(e.other)} tone="expense" />
                {t && (t.vat > 0 || t.tax > 0) && (
                  <>
                    <Tile label="НДС" hint="Считается «в том числе» от выручки по цене реализации: ставка ÷ (100 + ставка)." value={money(t.vat)} sub={`${t.vat_rate} % в том числе`} tone="expense" />
                    <Tile
                      label="Налог"
                      value={money(t.tax)}
                      sub={t.min_tax_applied
                        ? `${t.tax_mode_label} · минимальный ${t.min_tax_percent} % от доходов`
                        : `${t.tax_mode_label}, ${t.tax_rate} %`}
                      tone="expense"
                      hint="Налог с дохода по выбранному режиму. База — выручка по цене реализации за вычетом НДС: соинвестиция Ozon в неё не входит. На «Доходы минус расходы» не опускается ниже минимального: 1 % от доходов на УСН и 3 % на АУСН, даже при убытке."
                    />
                  </>
                )}
              </div>
              {dashboard.expense_types?.length > 0 && (
                <table className="ue-types">
                  <thead>
                    <tr>
                      <th>Начисление Ozon</th>
                      <th>Группа</th>
                      <th className="ue-num">Отправлений</th>
                      <th className="ue-num">Сумма, ₽</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.expense_types.map((x) => (
                      <tr key={`${x.type_id}-${x.name}`}>
                        <td>{x.name}</td>
                        <td><span className="ue-types__bucket">{e.labels?.[x.bucket] || x.bucket}</span></td>
                        <td className="ue-num">{x.postings || '—'}</td>
                        <td className="ue-num">{Math.round(x.amount).toLocaleString('ru-RU')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="ue-note">
                Комиссия Ozon включает соинвестицию маркетплейса, поэтому её доля от выручки
                продавца доходит до 50 % — это ставка из отчёта, а не ошибка расчёта.
              </div>
            </div>
          </>
        )
      )}

      {/* ── экономика по товарам ───────────────────────────────────────── */}
      {sheet === 'breakdown' && (
        <>
          <div className="ue-group">
            <button
              type="button"
              className={`ue-period__preset${groupBy === 'sku' ? ' ue-period__preset--on' : ''}`}
              onClick={() => setGroupBy('sku')}
            >
              По артикулам
            </button>
            <button
              type="button"
              className={`ue-period__preset${groupBy === 'model' ? ' ue-period__preset--on' : ''}`}
              onClick={() => setGroupBy('model')}
            >
              По моделям
            </button>
          </div>
          {breakdown?.allocated?.total > 0 && (
            <div className="ue-note">
              {Math.round(breakdown.allocated.total).toLocaleString('ru-RU')} ₽ расходов Ozon
              не привязывает к товару: начисления без SKU
              ({Math.round(breakdown.allocated.ozon_fees_total).toLocaleString('ru-RU')} ₽), налоги
              ({Math.round(breakdown.allocated.taxes).toLocaleString('ru-RU')} ₽) и иные расходы
              ({Math.round(breakdown.allocated.other_expenses).toLocaleString('ru-RU')} ₽) считаются
              на весь кабинет. Они разнесены по строкам, иначе итог таблицы не сошёлся бы
              с дашбордом.
              {breakdown.allocated.ads_basis === 'ad_spend'
                ? ' Реклама разнесена по фактическим тратам на SKU из модуля Рекламы, остальное — пропорционально выручке.'
                : ' Всё разнесено пропорционально выручке: данных по рекламе на уровне SKU за период нет.'}
              {' '}Колонка «Прибыль прямая» считается без общих — по ней решают, держать товар
              или выводить; «Прибыль полная» отвечает, сколько заработали.
            </div>
          )}
          <UnifiedTable
            tableId={`ozon-unit-economics-${groupBy}`}
            columns={columns}
            data={breakdown?.rows || []}
            loading={busy}
            getRowKey={(row) => row.key}
            search={tableSearch}
            onSearchChange={setTableSearch}
            exportFilename={`unit-economics-${groupBy}-${dateFrom}_${dateTo}`}
            onExportCsv
            onCopyTable
          />
        </>
      )}

      {/* ── себестоимость ──────────────────────────────────────────────── */}
      {sheet === 'cogs' && (
        !cogs ? (
          <div className="ue-empty">{busy ? 'Загружаю…' : 'Нет данных.'}</div>
        ) : (
          <>
            <div className="ue-cogsbar">
              <input
                type="text" className="ue-cogsbar__search" placeholder="Артикул, SKU или название"
                value={cogsSearch} onChange={(ev) => setCogsSearch(ev.target.value)}
              />
              <span className="ue-period__state">
                Заполнено {cogs.filled} из {cogs.total} · покрыто {pct(cogs.coverage)} проданных единиц
              </span>
              <div className="ue-period__spacer" />
              <button type="button" onClick={downloadCogsTemplate}>Скачать XLSX</button>
              <button type="button" onClick={() => cogsFileRef.current?.click()}>
                Загрузить XLSX
              </button>
              <input
                ref={cogsFileRef} type="file" accept=".xlsx,.xls"
                style={{ display: 'none' }} onChange={importCogsXlsx}
              />
              <button
                type="button" onClick={saveCogs}
                disabled={!Object.keys(cogsEdits).length}
              >
                Сохранить{Object.keys(cogsEdits).length ? ` (${Object.keys(cogsEdits).length})` : ''}
              </button>
            </div>
            <div className="ue-note">
              Это та же себестоимость, что и в Репрайсере → «Цена под рентабельность»: таблица
              одна, заполненное здесь сразу видно там и наоборот. Сверху идут товары, которые
              продавались в периоде, но себестоимости не имеют — именно они искажают прибыль.
              Товар определяется по SKU, артикул показан текущий: если его переименовали,
              себестоимость заполняется один раз, а в XLSX достаточно колонки SKU.
            </div>
            <table className="ue-types ue-cogs">
              <thead>
                <tr>
                  <th style={{ width: 52 }}>Фото</th>
                  <th style={{ width: 180 }}>Артикул</th>
                  <th style={{ width: 120 }}>SKU</th>
                  <th>Товар</th>
                  <th className="ue-num" style={{ width: 110 }}>Продано, шт</th>
                  <th className="ue-num" style={{ width: 140 }}>Выручка, ₽</th>
                  <th className="ue-num" style={{ width: 140 }}>Себестоимость, ₽</th>
                  <th className="ue-num" style={{ width: 150 }}>Себестоимость за период, ₽</th>
                </tr>
              </thead>
              <tbody>
                {cogs.rows
                  .filter((r) => {
                    return matchesSearch(r, cogsSearch);
                  })
                  .map((r) => {
                    const edited = cogsEdits[r.offer_id];
                    const value = edited !== undefined ? edited : (r.cogs ?? '');
                    const missing = r.units > 0 && !value;
                    return (
                      <tr key={r.offer_id} className={missing ? 'ue-cogs__row--missing' : ''}>
                        <td>
                          {r.image
                            ? <img src={r.image} alt="" className="ue-cogs__img" loading="lazy" />
                            : <span className="ue-cell-empty">—</span>}
                        </td>
                        <td>
                          {r.offer_id}
                          {r.old_offer_ids?.length ? (
                            <div className="ue-cell-empty" title="Под этим артикулом товар шёл в отчётах периода">
                              ранее: {r.old_offer_ids.join(', ')}
                            </div>
                          ) : null}
                        </td>
                        <td>{r.sku || '—'}</td>
                        <td>{r.name}</td>
                        <td className="ue-num">{r.units || '—'}</td>
                        <td className="ue-num">{r.revenue ? Math.round(r.revenue).toLocaleString('ru-RU') : '—'}</td>
                        <td className="ue-num">
                          <input
                            type="number" min="0" step="0.01" value={value}
                            onChange={(ev) => setCogsEdits({ ...cogsEdits, [r.offer_id]: ev.target.value })}
                          />
                        </td>
                        <td className="ue-num">
                          {value && r.units
                            ? Math.round(Number(value) * r.units).toLocaleString('ru-RU')
                            : '—'}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </>
        )
      )}

      {/* ── налоги и иные расходы ──────────────────────────────────────── */}
      {sheet === 'setup' && (
        !settings ? (
          <div className="ue-empty">Загружаю настройки…</div>
        ) : (
          <>
            <div className="ue-block">
              <div className="ue-block__title">Налогообложение</div>
              <div className="ue-form">
                <label className="ue-field">
                  <span>Режим</span>
                  <select
                    value={settings.tax_mode}
                    onChange={(ev) => {
                      const mode = ev.target.value;
                      // ставку подставляем только если она ещё не задана вручную
                      const rate = Number(settings.tax_rate) > 0
                        ? Number(settings.tax_rate)
                        : (DEFAULT_TAX_RATE[mode] ?? 0);
                      saveSettings({ tax_mode: mode, tax_rate: rate });
                    }}
                  >
                    {TAX_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <label className="ue-field">
                  <span>Ставка налога, %</span>
                  <input
                    type="number" min="0" max="100" step="0.1" value={settings.tax_rate}
                    onChange={(ev) => setSettings({ ...settings, tax_rate: ev.target.value })}
                    onBlur={(ev) => saveSettings({ tax_rate: Number(ev.target.value) || 0 })}
                  />
                </label>
                {MIN_TAX_PERCENT[settings.tax_mode] && (
                  <div className="ue-field ue-field--note">
                    Минимальный налог {MIN_TAX_PERCENT[settings.tax_mode]} % от доходов —
                    платится даже при убытке
                  </div>
                )}
                <label className="ue-field ue-field--check">
                  <input
                    type="checkbox" checked={settings.vat_enabled}
                    onChange={(ev) => saveSettings({ vat_enabled: ev.target.checked })}
                  />
                  <span>Плательщик НДС</span>
                </label>
                {settings.vat_enabled && (
                  <label className="ue-field">
                    <span>Ставка НДС, %</span>
                    <input
                      type="number" min="0" max="100" step="0.1" value={settings.vat_rate}
                      onChange={(ev) => setSettings({ ...settings, vat_rate: ev.target.value })}
                      onBlur={(ev) => saveSettings({ vat_rate: Number(ev.target.value) || 0 })}
                    />
                  </label>
                )}
              </div>
              <div className="ue-note">
                База и для налога, и для НДС — выручка по цене реализации: сумма, которую
                фактически заплатил покупатель, то есть цена продавца минус баллы Ozon.
                Соинвестиция в базу не входит. НДС считается «в том числе»: с 1 000 ₽ при
                ставке 5 % это 47,62 ₽, а не 50 ₽. На «Доходы минус расходы» налог не опускается ниже
                минимального — 1 % от доходов на УСН и 3 % на АУСН, независимо от того,
                вышел период в прибыль или в убыток.
              </div>
            </div>

            <div className="ue-block">
              <div className="ue-block__title">Иные расходы</div>
              <table className="ue-types ue-other">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}>Учёт</th>
                    <th>Название</th>
                    <th style={{ width: 150 }}>Период</th>
                    <th className="ue-num" style={{ width: 130 }}>Сумма, ₽</th>
                    <th className="ue-num" style={{ width: 150 }}>За выбранный период, ₽</th>
                    <th style={{ width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {(expenses?.items || []).map((row) => (
                    <tr key={row.id} className={row.is_counted ? '' : 'ue-other__row--off'}>
                      <td>
                        <input
                          type="checkbox" checked={row.is_counted}
                          onChange={(ev) => saveExpense(row, { is_counted: ev.target.checked })}
                          title="Учитывать в расходах и прибыли"
                        />
                      </td>
                      <td>{row.title}</td>
                      <td>
                        {row.kind === 'monthly'
                          ? 'ежемесячно'
                          : `разово ${ru(row.spend_date)}`}
                      </td>
                      <td className="ue-num">{Math.round(row.amount).toLocaleString('ru-RU')}</td>
                      <td className="ue-num">{Math.round(row.period_amount).toLocaleString('ru-RU')}</td>
                      <td>
                        <button type="button" className="ue-other__del" onClick={() => removeExpense(row)}>×</button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td />
                    <td>
                      <input
                        type="text" placeholder="Офис, ФОТ, подписки…" value={draft.title}
                        onChange={(ev) => setDraft({ ...draft, title: ev.target.value })}
                      />
                    </td>
                    <td>
                      <select
                        value={draft.kind}
                        onChange={(ev) => setDraft({ ...draft, kind: ev.target.value })}
                      >
                        <option value="monthly">ежемесячно</option>
                        <option value="onetime">разово</option>
                      </select>
                      {draft.kind === 'onetime' && (
                        <input
                          type="date" value={draft.spend_date || ''}
                          onChange={(ev) => setDraft({ ...draft, spend_date: ev.target.value })}
                        />
                      )}
                    </td>
                    <td className="ue-num">
                      <input
                        type="number" min="0" step="1" value={draft.amount}
                        onChange={(ev) => setDraft({ ...draft, amount: ev.target.value })}
                      />
                    </td>
                    <td className="ue-num">—</td>
                    <td>
                      <button type="button" onClick={addExpense}>+</button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="ue-note">
                Ежемесячная сумма попадает в период пропорционально числу дней, разовая — если её
                дата внутри периода. Строка без галочки остаётся в списке, но в прибыль не идёт.
                {expenses?.ignored > 0 && ` Сейчас не учитывается ${Math.round(expenses.ignored).toLocaleString('ru-RU')} ₽.`}
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}

export default OzonUnitEconomics;
